import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';

import { displayUrl } from '/src/utils/url';

import { adPlatformAdPreviewQuery } from '../queries';

const OVERLAY_WIDTH = 560;
const DEFAULT_OVERLAY_HEIGHT = 820;
const VIEWPORT_PADDING = 16;
const GAP_FROM_TRIGGER = 10;
const ARROW_SIZE = 12;

export function AdPreviewOverlay({
  siteId,
  adId,
  fallbackAdName,
  anchorRect,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
}) {
  const overlayRef = useRef(null);
  const [overlayPosition, setOverlayPosition] = useState(null);
  const { data, error, isLoading, isFetching } = useQuery({
    ...adPlatformAdPreviewQuery({
      siteId,
      adId,
    }),
  });
  const payload = data?.adPlatformAdPreview;
  const preview = payload?.preview;
  const ad = payload?.ad;
  const overlayMaxHeight = getOverlayMaxHeight();

  useLayoutEffect(() => {
    if (!anchorRect || typeof window === 'undefined') {
      return;
    }

    const updatePosition = () => {
      const measuredDimensions = measureOverlayDimensions(overlayRef.current);

      setOverlayPosition(getOverlayPosition(anchorRect, measuredDimensions));
    };

    updatePosition();

    if (!window.ResizeObserver || !overlayRef.current) {
      return undefined;
    }

    const resizeObserver = new window.ResizeObserver(updatePosition);
    resizeObserver.observe(overlayRef.current);

    return () => resizeObserver.disconnect();
  }, [
    ad?.name,
    anchorRect,
    error,
    isFetching,
    isLoading,
    preview?.body,
    preview?.cards,
    preview?.descriptions,
    preview?.headline,
    preview?.headlines,
    preview?.imageUrl,
    preview?.mediaType,
    preview?.path1,
    preview?.path2,
    preview?.template,
    preview?.videoUrl,
  ]);

  if (typeof document === 'undefined' || !anchorRect) {
    return null;
  }

  const position = overlayPosition || {
    arrowStyle: { visibility: 'hidden' },
    overlayStyle: {
      left: VIEWPORT_PADDING,
      top: VIEWPORT_PADDING,
      visibility: 'hidden',
    },
  };

  return createPortal(
    <div
      ref={overlayRef}
      data-testid="ad-preview-overlay"
      className="fixed z-[200] w-[min(560px,calc(100vw-32px))]"
      style={position.overlayStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className="absolute -left-1.5 h-3 w-3 rotate-45 border border-mystic-500 border-b-0 border-r-0 bg-white"
        style={position.arrowStyle}
      />
      <div
        data-testid="ad-preview-frame"
        className={[
          'overflow-y-auto overflow-x-hidden rounded-2xl border border-mystic-500 bg-white',
          'shadow-[0_18px_48px_rgba(21,35,60,0.2)]',
        ].join(' ')}
        style={{ maxHeight: overlayMaxHeight }}
      >
        {isLoading || isFetching ? (
          <OverlayState title={fallbackAdName} body="Loading preview..." />
        ) : error ? (
          <OverlayState title={fallbackAdName} body="We couldn't load this preview right now." tone="error" />
        ) : !preview?.previewSupported ? (
          <OverlayState title={ad?.name || fallbackAdName} body="Preview is not available for this platform yet." />
        ) : preview.template === 'META_FEED' ? (
          <MetaFeedPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_SEARCH' ? (
          <GoogleSearchPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_DISPLAY' ? (
          <GoogleDisplayPreview ad={ad} preview={preview} />
        ) : preview.template === 'GOOGLE_VIDEO' ? (
          <GoogleVideoPreview ad={ad} preview={preview} />
        ) : preview.template === 'REDDIT_FEED' ? (
          <RedditFeedPreview ad={ad} preview={preview} />
        ) : (
          <OverlayState title={ad?.name || fallbackAdName} body="Preview is not available for this template yet." />
        )}
      </div>
    </div>,
    document.body,
  );
}

function OverlayState({ title, body, tone = 'default' }) {
  return (
    <div className="p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-lynch-500">Ad Preview</p>
      <h3 className="mt-3 text-base font-semibold text-black-pearl-500">{title}</h3>
      <p className={`mt-3 text-sm ${tone === 'error' ? 'text-red-700' : 'text-lynch-500'}`}>{body}</p>
    </div>
  );
}

function MetaFeedPreview({ ad, preview }) {
  const cards = Array.isArray(preview.cards) ? preview.cards.filter(Boolean) : [];
  const primaryCard = cards[0];
  const primaryText = preview.body || preview.headline || ad?.name;
  const headline = preview.headline || primaryCard?.headline || ad?.name;
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);
  const advertiserName = preview.advertiserName || ad?.name || 'Facebook ad';
  const footerHeadline = primaryCard?.headline || headline;
  const footerBody = primaryCard?.body || preview.descriptions?.[0] || null;
  const footerDestinationUrl = primaryCard?.destinationUrl || preview.destinationUrl;
  const footerDisplayUrl =
    primaryCard?.displayUrl || resolvedDisplayUrl || (footerDestinationUrl ? displayUrl(footerDestinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || advertiserName} preview`} className="bg-[#F0F2F5]">
      <div className="border-b border-solitude-500 bg-white px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Facebook Feed'}
        </p>
      </div>

      <div className="px-4 py-4">
        <div className="overflow-hidden rounded-[26px] border border-[#D8DEE8] bg-white shadow-[0_18px_40px_rgba(20,34,59,0.12)]">
          <div className="px-4 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <div
                  className={[
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
                    'bg-[#E7F3FF] text-[17px] font-semibold text-[#1B74E4]',
                  ].join(' ')}
                >
                  {(advertiserName || 'A').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-semibold text-[#050505]">{advertiserName}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-[13px] text-[#65676B]">
                    <span>Ad</span>
                    <span>&middot;</span>
                    <span>Public</span>
                  </div>
                  {ad?.campaignName ? (
                    <div className="mt-1 truncate text-[13px] text-[#65676B]">{ad.campaignName}</div>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-2 text-[#050505]">
                <MetaIconButton ariaLabel="More options">
                  <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
                    <circle cx="4" cy="10" r="1.6" />
                    <circle cx="10" cy="10" r="1.6" />
                    <circle cx="16" cy="10" r="1.6" />
                  </svg>
                </MetaIconButton>
                <MetaIconButton ariaLabel="Close preview">
                  <svg viewBox="0 0 20 20" className="h-4 w-4 stroke-current" aria-hidden="true">
                    <path d="M5 5L15 15M15 5L5 15" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </MetaIconButton>
              </div>
            </div>

            {primaryText ? <MetaPrimaryCopy text={primaryText} /> : null}
          </div>

          {cards.length ? (
            <MetaCarouselStage advertiserName={advertiserName} cards={cards} />
          ) : (
            <div className="px-3.5 pb-3.5">
              <MetaDestinationCard
                advertiserName={advertiserName}
                body={footerBody}
                displayUrl={footerDisplayUrl}
                headline={footerHeadline}
                destinationUrl={footerDestinationUrl}
              >
                <PreviewMedia
                  preview={preview}
                  alt={headline || advertiserName}
                  iframeTitle={`${headline || advertiserName} video preview`}
                  emptyState="No synced media is available for this creative yet."
                  className="h-[392px] w-full object-cover"
                  emptyStateClassName="h-[392px] bg-[#E9EFF7] text-[#65676B]"
                />
              </MetaDestinationCard>
            </div>
          )}

          <MetaActionBar />
        </div>
      </div>
    </article>
  );
}

function MetaIconButton({ ariaLabel, children }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      tabIndex={-1}
      className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[#F2F3F5]"
    >
      {children}
    </button>
  );
}

function MetaPrimaryCopy({ text }) {
  return (
    <div className="pb-3 pt-3">
      <p className="text-[16px] leading-7 text-[#050505]">
        {text}
        {text.length > 72 ? <span className="ml-2 font-semibold text-[#65676B]">...see more</span> : null}
      </p>
    </div>
  );
}

function MetaCarouselStage({ advertiserName, cards }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeCard = cards[activeIndex] || cards[0];
  const previousCard = activeIndex > 0 ? cards[activeIndex - 1] : null;
  const nextCard = activeIndex < cards.length - 1 ? cards[activeIndex + 1] : null;

  return (
    <div className="px-3.5 pb-3.5">
      <div className="relative flex items-stretch gap-2">
        <MetaCarouselPeekCard card={previousCard} ariaHidden />

        <div className="min-w-0 flex-1">
          <MetaDestinationCard
            advertiserName={advertiserName}
            body={activeCard?.body}
            displayUrl={
              activeCard?.displayUrl || (activeCard?.destinationUrl ? displayUrl(activeCard.destinationUrl) : null)
            }
            headline={activeCard?.headline || `Card ${activeIndex + 1}`}
            destinationUrl={activeCard?.destinationUrl}
          >
            <PreviewMedia
              preview={activeCard || {}}
              alt={activeCard?.headline || `Card ${activeIndex + 1}`}
              iframeTitle={`${activeCard?.headline || `Card ${activeIndex + 1}`} card preview`}
              emptyState="No synced media is available for this card yet."
              className="h-[340px] w-full bg-[#F0F2F5] object-contain"
              emptyStateClassName="h-[340px] bg-[#E9EFF7] text-[#65676B]"
            />
          </MetaDestinationCard>
        </div>

        <MetaCarouselPeekCard card={nextCard} ariaHidden />

        {previousCard ? (
          <MetaCarouselArrow
            ariaLabel="Previous carousel card"
            direction="left"
            onClick={() => setActiveIndex((index) => Math.max(index - 1, 0))}
          />
        ) : null}

        {nextCard ? (
          <MetaCarouselArrow
            ariaLabel="Next carousel card"
            direction="right"
            onClick={() => setActiveIndex((index) => Math.min(index + 1, cards.length - 1))}
          />
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-center gap-1.5">
        {cards.map((card, index) => (
          <button
            key={`${card?.headline || index}-dot`}
            type="button"
            aria-label={`Show carousel card ${index + 1}`}
            aria-pressed={activeIndex === index}
            onClick={() => setActiveIndex(index)}
            className={[
              'h-2.5 rounded-full transition-all',
              activeIndex === index ? 'w-5 bg-[#1B74E4]' : 'w-2.5 bg-[#CCD0D5]',
            ].join(' ')}
          />
        ))}
      </div>
    </div>
  );
}

function MetaDestinationCard({ advertiserName, body, children, displayUrl, headline, destinationUrl }) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[#DADDE1] bg-white">
      <div className="overflow-hidden rounded-t-[22px]">{children}</div>

      <div className="border-t border-[#DADDE1] bg-[#F0F2F5] px-3.5 py-3.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-[#65676B]">
              {displayUrl || advertiserName || 'Destination URL'}
            </div>
            <div className="mt-1.5 text-[17px] font-semibold leading-7 text-[#050505]">
              {headline || 'Untitled creative'}
            </div>
            {body ? (
              <p
                className="mt-1 text-[14px] leading-5 text-[#65676B]"
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                }}
              >
                {body}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            tabIndex={-1}
            className="shrink-0 rounded-[18px] bg-[#E4E6EB] px-3.5 py-2.5 text-[14px] font-semibold text-[#050505]"
          >
            Learn more
          </button>
        </div>

        {destinationUrl ? <div className="mt-2 truncate text-[14px] text-[#1877F2]">{destinationUrl}</div> : null}
      </div>
    </div>
  );
}

function MetaCarouselPeekCard({ card, ariaHidden = false }) {
  if (!card) {
    return <div className="w-[74px] shrink-0" aria-hidden={ariaHidden} />;
  }

  return (
    <div
      aria-hidden={ariaHidden}
      className="w-[74px] shrink-0 overflow-hidden rounded-[22px] border border-[#DADDE1] bg-white"
    >
      <PreviewMedia
        preview={card}
        alt={card.headline || 'Carousel card'}
        iframeTitle={`${card.headline || 'Carousel card'} side preview`}
        emptyState="No synced media is available for this card yet."
        className="h-[340px] w-full bg-[#F0F2F5] object-contain"
        emptyStateClassName="h-[340px] bg-[#E9EFF7] text-[#65676B]"
      />
      <div className="border-t border-[#DADDE1] bg-[#F0F2F5] px-3 py-3">
        <div className="h-14 rounded-[14px] bg-[#E4E6EB]" />
      </div>
    </div>
  );
}

function MetaCarouselArrow({ ariaLabel, direction, onClick }) {
  const isLeft = direction === 'left';

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={[
        'absolute top-[158px] flex h-11 w-11 items-center justify-center rounded-full border border-white/70',
        'bg-white/95 text-[#65676B] shadow-[0_8px_24px_rgba(0,0,0,0.18)] transition-colors hover:bg-white',
        isLeft ? 'left-[44px]' : 'right-[44px]',
      ].join(' ')}
    >
      <svg viewBox="0 0 24 24" className="h-6 w-6 stroke-current" aria-hidden="true">
        <path
          d={isLeft ? 'M14.5 5L8 12L14.5 19' : 'M9.5 5L16 12L9.5 19'}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function MetaActionBar() {
  return (
    <div className="border-t border-[#CED0D4] px-3 py-2.5">
      <div className="grid grid-cols-3 gap-2">
        <MetaActionButton label="Like">
          <svg viewBox="0 0 20 20" className="h-5 w-5 stroke-current" aria-hidden="true">
            <path
              d="M6.5 9.5V17H3.5V9.5H6.5ZM8.5 17H14.1C15 17 15.7 16.4 15.8 15.6L16.7 10.2C16.8 9.1 15.9 8.2 14.8 8.2H11.6L12 5.7C12.1 4.7 11.3 3.8 10.3 3.8L8.5 8.1V17Z"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </MetaActionButton>
        <MetaActionButton label="Comment">
          <svg viewBox="0 0 20 20" className="h-5 w-5 stroke-current" aria-hidden="true">
            <path
              d="M4.5 5.5H15.5C16.1 5.5 16.5 5.9 16.5 6.5V12.5C16.5 13.1 16.1 13.5 15.5 13.5H9L5 16V13.5H4.5C3.9 13.5 3.5 13.1 3.5 12.5V6.5C3.5 5.9 3.9 5.5 4.5 5.5Z"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </MetaActionButton>
        <MetaActionButton label="Share">
          <svg viewBox="0 0 20 20" className="h-5 w-5 stroke-current" aria-hidden="true">
            <path
              d="M12.5 5.5L16 9L12.5 12.5M15.5 9H8.5C6.6 9 5 10.6 5 12.5V14.5"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </MetaActionButton>
      </div>
    </div>
  );
}

function MetaActionButton({ children, label }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      className="flex items-center justify-center gap-2 rounded-xl px-2.5 py-1.5 text-[14px] font-semibold text-[#65676B] transition-colors hover:bg-[#F2F3F5]"
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

function GoogleSearchPreview({ ad, preview }) {
  const headlines = preview.headlines?.length
    ? preview.headlines.slice(0, 3)
    : [preview.headline || ad?.name].filter(Boolean);
  const descriptions = preview.descriptions?.length ? preview.descriptions.slice(0, 2) : [preview.body].filter(Boolean);
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google ad'} preview`} className="bg-[#F8FAFD]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Google Search'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="rounded-2xl border border-[#E3E9F5] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1A73E8] text-sm font-semibold text-white">
              {(ad?.name || 'G').slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-black-pearl-500">
                {resolvedDisplayUrl || 'googlead.example'}
              </div>
              <div className="mt-0.5 text-xs text-lynch-500">Sponsored</div>
            </div>
          </div>

          {headlines.length ? (
            <div className="mt-4 text-[22px] leading-8 text-[#1A0DAB]">
              {headlines.map((headline, index) => (
                <span key={`${headline}-${index}`}>
                  {index > 0 ? <span className="mx-2 text-[#5F6368]">|</span> : null}
                  {headline}
                </span>
              ))}
            </div>
          ) : null}

          {resolvedDisplayUrl ? <div className="mt-2 text-sm text-[#188038]">{resolvedDisplayUrl}</div> : null}

          {descriptions.length ? (
            <div className="mt-3 space-y-1 text-[15px] leading-6 text-[#4D5156]">
              {descriptions.map((description, index) => (
                <p key={`${description}-${index}`}>{description}</p>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-lynch-500">No synced copy is available for this search ad yet.</div>
          )}
        </div>
      </div>
    </article>
  );
}

function GoogleDisplayPreview({ ad, preview }) {
  const headline = preview.headline || ad?.name || preview.displayUrl || 'Display creative';
  const body =
    preview.body || preview.descriptions?.[0] || 'Preview copy is not available for this display creative yet.';
  const advertiserName = preview.advertiserName || preview.displayUrl || 'Google Display';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google display ad'} preview`} className="bg-[#F8FAFD]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Google Display'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#E3E9F5] bg-white shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8F0FE] text-sm font-semibold text-[#1A73E8]">
                Ad
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-black-pearl-500">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-lynch-500">Google Ads</div>
              </div>
            </div>
          </div>

          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} media preview`}
            emptyState="No synced image or video is available for this display creative yet."
            className="h-[220px] w-full object-cover"
            emptyStateClassName="h-[220px] bg-[#EEF3FB] text-[#5F6368]"
          />

          <div className="px-4 py-4">
            {resolvedDisplayUrl ? <div className="text-xs text-[#188038]">{resolvedDisplayUrl}</div> : null}
            <div className="mt-2 text-lg font-semibold leading-7 text-black-pearl-500">{headline}</div>
            <p className="mt-2 text-[15px] leading-6 text-[#4D5156]">{body}</p>
          </div>
        </div>
      </div>
    </article>
  );
}

function GoogleVideoPreview({ ad, preview }) {
  const headline = preview.headline || ad?.name || preview.displayUrl || 'Video ad';
  const body = preview.body || preview.descriptions?.[0] || 'Preview copy is not available for this video ad yet.';
  const advertiserName = preview.advertiserName || preview.displayUrl || 'YouTube ad';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || 'Google video ad'} preview`} className="bg-[#0F1115]">
      <div className="border-b border-[#20242C] px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#B3BDCC]">
          {preview.placementLabel || 'YouTube'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#20242C] bg-[#181C24] shadow-[0_18px_44px_rgba(0,0,0,0.35)]">
          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} video preview`}
            emptyState="No synced video asset is available for this creative yet."
            className="aspect-video w-full bg-black object-cover"
            emptyStateClassName="aspect-video bg-black text-[#B3BDCC]"
          />

          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FF0033] text-xs font-semibold uppercase text-white">
                YT
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-[#B3BDCC]">Sponsored video</div>
              </div>
            </div>

            <div className="mt-4 text-[17px] font-semibold leading-7 text-white">{headline}</div>
            <p className="mt-2 text-sm leading-6 text-[#D6DBE4]">{body}</p>
            {resolvedDisplayUrl ? <div className="mt-3 text-sm text-[#7EE787]">{resolvedDisplayUrl}</div> : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function RedditFeedPreview({ ad, preview }) {
  const advertiserName = preview.advertiserName || 'u/promoted';
  const headline = preview.headline || ad?.name || 'Promoted post';
  const resolvedDisplayUrl = preview.displayUrl || (preview.destinationUrl ? displayUrl(preview.destinationUrl) : null);

  return (
    <article aria-label={`${ad?.name || advertiserName} preview`} className="bg-[#F6F7F8]">
      <div className="border-b border-solitude-500 bg-white px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-lynch-500">
          {preview.placementLabel || 'Reddit Feed'}
        </p>
      </div>

      <div className="px-5 py-5">
        <div className="overflow-hidden rounded-2xl border border-[#D7DADF] bg-white shadow-[0_10px_24px_rgba(21,35,60,0.08)]">
          <div className="px-4 py-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-sm font-semibold text-white">
                {(advertiserName || 'R').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-black-pearl-500">{advertiserName}</div>
                <div className="mt-0.5 text-xs text-lynch-500">Promoted</div>
              </div>
            </div>

            <div className="mt-4 text-[18px] font-semibold leading-7 text-[#1A1A1B]">{headline}</div>
            {preview.body ? <p className="mt-2 text-[15px] leading-6 text-[#1A1A1B]">{preview.body}</p> : null}
          </div>

          <PreviewMedia
            preview={preview}
            alt={headline}
            iframeTitle={`${headline} media preview`}
            emptyState="No synced media is available for this promoted post yet."
            className="max-h-[280px] w-full object-cover"
            emptyStateClassName="h-[220px] bg-[#F2F4F5] text-[#6A6F77]"
          />

          {resolvedDisplayUrl ? (
            <div className="border-t border-[#E9EBED] px-4 py-3 text-sm text-[#6A6F77]">{resolvedDisplayUrl}</div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function PreviewMedia({ preview, alt, iframeTitle, emptyState, className = '', emptyStateClassName = '' }) {
  const youtubeEmbedUrl = getYouTubeEmbedUrl(preview.videoUrl);

  if (youtubeEmbedUrl) {
    return (
      <div className={['w-full bg-black', className].join(' ')}>
        <iframe
          title={iframeTitle}
          className="h-full w-full border-0"
          src={youtubeEmbedUrl}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (preview.mediaType === 'VIDEO' && preview.videoUrl) {
    return <video className={['bg-black', className].join(' ')} controls muted playsInline src={preview.videoUrl} />;
  }

  if (preview.mediaType === 'IMAGE' && preview.imageUrl) {
    return <img className={className} alt={alt} src={preview.imageUrl} />;
  }

  return (
    <div className={['flex items-center justify-center px-6 text-center text-sm', emptyStateClassName].join(' ')}>
      {emptyState}
    </div>
  );
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./u, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId = parsed.searchParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.replace(/^\/+/u, '');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
  } catch {
    return null;
  }

  return null;
}

function getOverlayPosition(anchorRect, { height, width }) {
  const viewportWidth = window.innerWidth || width + VIEWPORT_PADDING * 2;
  const viewportHeight = window.innerHeight || height + VIEWPORT_PADDING * 2;

  const left = clamp(
    anchorRect.right + GAP_FROM_TRIGGER,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, viewportWidth - width - VIEWPORT_PADDING),
  );
  const top = clamp(
    anchorRect.top + anchorRect.height / 2 - height / 2,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, viewportHeight - height - VIEWPORT_PADDING),
  );
  const arrowTop = clamp(
    anchorRect.top + anchorRect.height / 2 - top - ARROW_SIZE / 2,
    VIEWPORT_PADDING,
    Math.max(VIEWPORT_PADDING, height - VIEWPORT_PADDING - ARROW_SIZE),
  );

  return {
    overlayStyle: {
      left,
      top,
      visibility: 'visible',
    },
    arrowStyle: {
      top: arrowTop,
    },
  };
}

function measureOverlayDimensions(node) {
  const rect = node?.getBoundingClientRect?.() || {};

  return {
    width: rect.width || OVERLAY_WIDTH,
    height: Math.min(rect.height || DEFAULT_OVERLAY_HEIGHT, getOverlayMaxHeight()),
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getOverlayMaxHeight() {
  if (typeof window === 'undefined') {
    return DEFAULT_OVERLAY_HEIGHT;
  }

  return Math.max(280, (window.innerHeight || DEFAULT_OVERLAY_HEIGHT + VIEWPORT_PADDING * 2) - VIEWPORT_PADDING * 2);
}
